import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { adminAPI } from '../services/api';
import NewsForm from '../components/forms/NewsForm';

const NewsFormPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;

  const [formData, setFormData] = useState({
    title: '',
    category: '',
    date: new Date().toISOString().split('T')[0],
    content: '',
    imageUrl: '',
    imageFile: null,
    isFeatured: false,
    isPublished: true,
    removeImage: false
  });

  const [submitting, setSubmitting] = useState(false);
  const [originalImageUrl, setOriginalImageUrl] = useState('');

  // Load news data if in edit mode
  useEffect(() => {
    if (isEditMode) {
      const loadNews = async () => {
        try {
          const newsItem = await adminAPI.getNewsById(id);
          const imageUrl = newsItem.image || newsItem.imageUrl || '';
          const formattedNews = {
            ...newsItem,
            imageUrl: imageUrl,
            imageFile: null,
            isFeatured: !!newsItem.featured,
            date: newsItem.date ? newsItem.date.split('T')[0] : '',
            removeImage: false
          };
          setFormData(formattedNews);
          setOriginalImageUrl(imageUrl);
        } catch (error) {
          console.error('Error loading news:', error);
          toast.error('Failed to load news data');
          navigate('/admin/dashboard');
        }
      };
      loadNews();
    }
  }, [id, isEditMode, navigate]);

  const handleSubmit = async (e) => {
    if (e && typeof e.preventDefault === 'function') {
      e.preventDefault();
    }
    
    setSubmitting(true);

    try {
      // Determine if image was removed
      const imageWasRemoved = isEditMode && originalImageUrl && !formData.imageUrl && !formData.imageFile;

      // ALWAYS use FormData (consistent with backend expectations)
      const formDataToSend = new FormData();
      
      // Add file if exists
      if (formData.imageFile) {
        formDataToSend.append('file', formData.imageFile);
      }

      // Add remove_image flag if image was removed
      if (imageWasRemoved) {
        formDataToSend.append('remove_image', 'true');
      }

      // Add all required fields
      formDataToSend.append('title', formData.title);
      formDataToSend.append('category', formData.category);
      formDataToSend.append('content', formData.content);
      formDataToSend.append('date', formData.date || new Date().toISOString().split('T')[0]);
      formDataToSend.append('is_featured', formData.isFeatured ? 'true' : 'false');

      if (isEditMode) {
        await adminAPI.updateNews(id, formDataToSend);
        toast.success('News updated successfully');
      } else {
        await adminAPI.createNews(formDataToSend);
        toast.success('News created successfully');
      }

      navigate('/admin/dashboard');
    } catch (error) {
      console.error('Error saving news:', error);
      const errorMessage = error.response?.data?.detail || 
                          error.response?.data?.message || 
                          error.message || 
                          'Failed to save news';
      toast.error(`Error: ${errorMessage}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/admin/dashboard');
  };

  return (
    <div className="container mx-auto p-6">
      <NewsForm
        formData={formData}
        setFormData={setFormData}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        submitting={submitting}
        isEdit={isEditMode}
      />
    </div>
  );
};

export default NewsFormPage;